import React, { useState, useRef, useEffect, useMemo } from 'react';
import { MapPin, CheckCircle2 } from 'lucide-react';
import { Input } from './ui/input';

export const ComuneAutocomplete = ({ onSelect }) => {
  const [query, setQuery] = useState('');
  const [comuni, setComuni] = useState(null);
  const [open, setOpen] = useState(false);
  const [matched, setMatched] = useState(null);
  const boxRef = useRef(null);

  const load = async () => {
    if (!comuni) {
      try {
        const res = await fetch('/data/comuni.json');
        setComuni(await res.json());
      } catch { setComuni([]); }
    }
  };

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const suggestions = useMemo(() => {
    if (!comuni || query.trim().length < 2) return [];
    const q = query.trim().toLowerCase();
    const starts = [];
    const contains = [];
    for (const c of comuni) {
      const n = c.n.toLowerCase();
      if (n.startsWith(q)) { starts.push(c); if (starts.length >= 8) break; }
      else if (contains.length < 4 && n.includes(q)) contains.push(c);
    }
    return [...starts, ...contains].slice(0, 8);
  }, [comuni, query]);

  const emit = (text, list) => {
    const exact = (list || comuni || []).find((c) => c.n.toLowerCase() === text.trim().toLowerCase());
    setMatched(exact || null);
    onSelect(exact ? { comune: exact.n, provincia: exact.p } : { comune: text.trim(), provincia: '' });
  };

  const pick = (c) => {
    setQuery(c.n);
    setMatched(c);
    setOpen(false);
    onSelect({ comune: c.n, provincia: c.p });
  };

  return (
    <div ref={boxRef} className="relative">
      <MapPin className="absolute left-3 top-[22px] -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
      <Input
        data-testid="grandine-form-comune"
        type="text"
        placeholder="In quale comune si trova l'immobile? *"
        value={query}
        onFocus={() => { load(); setOpen(true); }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); emit(e.target.value); }}
        required
        autoComplete="off"
        className="pl-10 h-12 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-xl focus:ring-2 focus:ring-green-600 focus:border-transparent"
      />
      {matched && (
        <p className="mt-1 flex items-center gap-1 text-xs text-green-700" data-testid="comune-matched">
          <CheckCircle2 className="w-3 h-3" /> {matched.n} ({matched.p})
        </p>
      )}
      {open && suggestions.length > 0 && (
        <ul data-testid="comune-suggestions" className="absolute z-[60] mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden max-h-64 overflow-y-auto">
          {suggestions.map((c) => (
            <li key={`${c.n}-${c.p}`}>
              <button type="button" onClick={() => pick(c)} className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-green-50 transition-colors">
                {c.n} <span className="text-slate-400 text-xs">({c.p})</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
